import { getMedplumClient } from './medplum';
import { apiClient } from './client';
import { createComponentLogger } from '@/lib/utils/logger';
import type {
  EncounterResource,
  CreateEncounterRequest,
  UpdateEncounterRequest,
  DiagnosticReportResource,
  CreateDiagnosticReportRequest,
  PractitionerResource,
  CreatePractitionerRequest,
  DiagnosticReportStatus,
} from '@/lib/types/encounter';
import type {
  Gender,
  PatientStatus,
  PatientListItem,
  PatientDemographics,
  PersonCandidate,
} from '@/lib/types/patient';
import { calculateAge } from '@/lib/types/patient';
import { phoneSearchValues, toE164India } from '@/lib/utils/phone';

const logger = createComponentLogger('medplumResources');

function normalizeResourceId(_resourceType: string, id: string): string {
  if (!id) {
    return id;
  }
  const parts = id.split('/');
  return parts[parts.length - 1] || id;
}

/**
 * Resolve practitioner name from ID by fetching the Practitioner resource
 */
async function resolvePractitionerName(practitionerId: string): Promise<string | undefined> {
  try {
    const practitioner = await medplumResourcesApi.getPractitioner(practitionerId);

    const name = practitioner.name?.[0] as any;
    if (name) {
      // Include prefix (e.g., "Dr"), given name, and family name
      const prefix = name.prefix?.join(' ') || '';
      const givenName = name.given?.join(' ') || '';
      const familyName = name.family || '';

      // Build full name with prefix
      const parts = [prefix, givenName, familyName].filter(Boolean);
      return parts.join(' ').trim() || undefined;
    }

    return undefined;
  } catch (error) {
    logger.error('Failed to resolve practitioner name for ID:', practitionerId, error);
    return undefined;
  }
}

function toResourceReference(resourceType: string, id: string): string {
  return id.includes('/') ? id : `${resourceType}/${id}`;
}

function mapMedplumGenderToApp(gender?: string): Gender {
  switch (gender?.toLowerCase()) {
    case 'male':
      return 'M';
    case 'female':
      return 'F';
    case 'other':
      return 'O';
    default:
      return 'U';
  }
}

function mapAppGenderToMedplum(gender?: Gender): 'male' | 'female' | 'other' | 'unknown' {
  switch (gender) {
    case 'M':
      return 'male';
    case 'F':
      return 'female';
    case 'O':
      return 'other';
    default:
      return 'unknown';
  }
}

function mapPatientStatus(resource: PatientResource): PatientStatus {
  if (resource.deceasedBoolean === true || !!resource.deceasedDateTime) {
    return 'deceased';
  }
  if (resource.active === false) {
    return 'inactive';
  }
  return 'active';
}

function getTelecomValue(resource: PatientResource, system: string): string | undefined {
  return resource.telecom?.find((entry) => entry.system === system && !!entry.value)?.value;
}

function getPrimaryAddress(resource: PatientResource) {
  const address = resource.address?.[0];
  return {
    addressLine: address?.line?.filter((line) => !!line).join(', ') || undefined,
    city: address?.city || undefined,
    state: address?.state || undefined,
    postalCode: address?.postalCode || undefined,
    country: address?.country || undefined,
  };
}

function buildPatientName(resource: PatientResource) {
  const nameEntry = resource.name?.[0];
  const givenNames = nameEntry?.given?.filter((part) => !!part) || [];
  const firstName = givenNames[0] || '';
  const lastName = nameEntry?.family || '';
  const text = nameEntry?.text?.trim();
  const textParts = text ? text.split(' ') : [];
  const fullName = [givenNames.join(' '), lastName].filter(
    (part) => part && part.trim().length > 0
  );
  const resolvedFullName = fullName.length > 0 ? fullName.join(' ').trim() : text || resource.id;
  return {
    firstName: firstName || textParts[0] || undefined,
    lastName: lastName || textParts.slice(1).join(' ') || undefined,
    fullName: resolvedFullName,
  };
}

function patientResourceToListItem(resource: PatientResource): PatientListItem {
  const identifier = resource.identifier?.[0]?.value || resource.id;
  const gender = mapMedplumGenderToApp(resource.gender);
  const birthDate = resource.birthDate;
  const age = birthDate ? calculateAge(birthDate) : 0;
  const phone = getTelecomValue(resource, 'phone');
  const status = mapPatientStatus(resource);
  const { fullName } = buildPatientName(resource);

  return {
    uuid: resource.id,
    identifier,
    name: fullName,
    age,
    gender,
    phoneNumber: phone,
    status,
    lastUpdated: resource.meta?.lastUpdated,
  };
}

function patientResourceToDemographics(resource: PatientResource): PatientDemographics {
  const identifier = resource.identifier?.[0]?.value || resource.id;
  const gender = mapMedplumGenderToApp(resource.gender);
  const birthDate = resource.birthDate || '';
  const age = birthDate ? calculateAge(birthDate) : 0;
  const phone = getTelecomValue(resource, 'phone');
  const email = getTelecomValue(resource, 'email');
  const { addressLine, city, state, postalCode, country } = getPrimaryAddress(resource);
  const { firstName, lastName, fullName } = buildPatientName(resource);
  const status = mapPatientStatus(resource);
  const timestamp = resource.meta?.lastUpdated || new Date().toISOString();

  // Extract marital status from FHIR CodeableConcept
  let maritalStatus: 'S' | 'M' | 'D' | 'W' | 'U' | undefined;
  const maritalStatusCode = resource.maritalStatus?.coding?.[0]?.code;
  if (maritalStatusCode) {
    // Map FHIR codes to app codes (UNK -> U for Unknown)
    if (maritalStatusCode === 'UNK') {
      maritalStatus = 'U';
    } else if (['S', 'M', 'D', 'W', 'U'].includes(maritalStatusCode)) {
      maritalStatus = maritalStatusCode as 'S' | 'M' | 'D' | 'W' | 'U';
    }
  }

  // Extract visit summary and lastUpdated from Patient.extension
  let visitSummary: string | undefined;
  let visitSummaryLastUpdated: string | undefined;
  const extensions = (
    resource as {
      extension?: Array<{
        url: string;
        extension?: Array<{ url: string; valueString?: string; valueDateTime?: string }>;
      }>;
    }
  ).extension;
  if (extensions) {
    const summaryExt = extensions.find(
      (ext) => ext.url === 'http://pranik.ai/fhir/StructureDefinition/patient-visit-summary'
    );
    if (summaryExt?.extension) {
      const summaryValueExt = summaryExt.extension.find((e) => e.url === 'summary');
      visitSummary = summaryValueExt?.valueString;
      const lastUpdatedExt = summaryExt.extension.find((e) => e.url === 'lastUpdated');
      visitSummaryLastUpdated = lastUpdatedExt?.valueDateTime;
    }
  }

  return {
    uuid: resource.id,
    identifier,
    name: fullName,
    firstName,
    lastName,
    dateOfBirth: birthDate,
    age,
    gender,
    maritalStatus,
    phoneNumber: phone,
    email,
    address: addressLine,
    city,
    state,
    postalCode,
    country,
    emergencyContactName: undefined,
    emergencyContactPhone: undefined,
    status,
    createdAt: timestamp,
    updatedAt: timestamp,
    visitSummary,
    visitSummaryLastUpdated,
  } as PatientDemographics;
}

/**
 * Strip HTML tags from an XHTML div string (used to extract plain text from
 * Composition.section.text.div). Block-level boundaries (list items,
 * paragraphs, line breaks) become newlines rather than being concatenated,
 * since DOM textContent inserts no separator between sibling elements.
 */
export function stripHtml(html: string): string {
  const withLineBreaks = html.replace(/<\/(li|p|div)>|<br\s*\/?>/gi, '\n');
  const doc = new DOMParser().parseFromString(withLineBreaks, 'text/html');
  const text = doc.body.textContent || '';
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

// These sections carry a JSON payload for parseAyurveda.ts, not narrative
// text, so they're excluded from the generic display list (SoapNoteData.sections)
// but still exposed via the keyed lookups below.
const STRUCTURED_DATA_ONLY_SECTIONS = new Set([
  'ayurveda context',
  'desa information',
  'ayurveda_context',
  'desa_information',
  'registration',
  'personal history',
  'personal_history',
  'vitals',
  'astha sthaana pareeksha',
  'astha_sthaana_pareeksha',
  'prakruthi dosha',
  'prakruthi_dosha',
  'diagnosis',
  'treatment',
]);

function compositionToSoapData(composition: CompositionResource): SoapNoteData {
  const result: SoapNoteData = {};

  // Extract all sections from the Composition as flat SoapSection[]
  if (composition.section) {
    const flatSections: SoapSection[] = [];
    composition.section.forEach((s) => {
      const title = s.title || s.code?.text;
      if (title && s.text?.div) {
        // Also store stripped plain-text version keyed by lowercase title
        const key = title.toLowerCase().replace(/\s+/g, '_');
        const stripped = stripHtml(s.text.div) || null;
        result[key] = stripped;
        result[title.toLowerCase()] = stripped;

        const codeText = s.code?.text?.toLowerCase();
        const isStructuredDataOnly =
          STRUCTURED_DATA_ONLY_SECTIONS.has(title.toLowerCase()) ||
          (!!codeText && STRUCTURED_DATA_ONLY_SECTIONS.has(codeText));

        if (!isStructuredDataOnly) {
          flatSections.push({
            title,
            content: s.text.div,
          });
        }
      }
    });
    result.sections = flatSections;
  }

  return result;
}

/**
 * Convert the kiosk's ClinicalImpression into the same SoapNoteData shape as a
 * Composition. The kiosk saves each SOAP section as one `note` entry formatted
 * "<Title>\n<text>" (see pranik-hmis kiosk.service.ts saveConsultation).
 */
export function clinicalImpressionToSoapData(
  ci: { note?: Array<{ text?: string }> } | null | undefined
): SoapNoteData | null {
  const section: NonNullable<CompositionResource['section']> = [];
  for (const note of ci?.note ?? []) {
    const raw = note.text ?? '';
    const newline = raw.indexOf('\n');
    if (newline === -1) continue;
    const title = raw.slice(0, newline).trim();
    const div = raw.slice(newline + 1).trim();
    if (title && div) section.push({ title, text: { div } });
  }
  if (section.length === 0) return null;
  return compositionToSoapData({ id: '', status: 'final', section });
}

function compositionToSummary(composition: CompositionResource): PatientDiagnosticReportSummary {
  const encounterReference = composition.encounter?.reference;

  // Extract practitioner info from author
  const authorEntry = composition.author?.[0];
  const practitionerId = authorEntry?.reference
    ? normalizeResourceId('Practitioner', authorEntry.reference)
    : undefined;
  const practitionerName = authorEntry?.display;

  return {
    id: composition.id,
    status: 'final' as DiagnosticReportStatus,
    codeText: composition.title || 'SOAP Note',
    issued: composition.date,
    encounterId: encounterReference
      ? normalizeResourceId('Encounter', encounterReference)
      : undefined,
    practitionerName,
    practitionerId,
  };
}

function diagnosticReportToSummary(
  report: DiagnosticReportResource
): PatientDiagnosticReportSummary {
  const codeText =
    report.code?.text ||
    report.code?.coding?.find((coding) => coding.display)?.display ||
    report.code?.coding?.[0]?.code ||
    report.id;

  const encounterReference = report.encounter?.reference;

  // Extract practitioner info from performer
  const performerEntry = report.performer?.[0];
  const practitionerId = performerEntry?.reference
    ? normalizeResourceId('Practitioner', performerEntry.reference)
    : undefined;
  const practitionerName = performerEntry?.display;

  return {
    id: report.id,
    status: report.status,
    codeText,
    issued: report.issued || report.effectiveDateTime,
    encounterId: encounterReference
      ? normalizeResourceId('Encounter', encounterReference)
      : undefined,
    encounterDisplay: report.encounter?.display,
    practitionerName,
    practitionerId,
  };
}

function documentReferenceToSummary(
  docRef: DocumentReferenceResource
): PatientDiagnosticReportSummary {
  const codeText = docRef.type?.text || docRef.type?.coding?.[0]?.display || 'Document';

  const encounterReference = docRef.context?.encounter?.[0]?.reference;

  return {
    id: docRef.id,
    status: 'final' as DiagnosticReportStatus,
    codeText,
    issued: docRef.date || docRef.meta?.lastUpdated,
    encounterId: encounterReference
      ? normalizeResourceId('Encounter', encounterReference)
      : undefined,
    encounterDisplay: docRef.context?.encounter?.[0]?.display,
  };
}

function diagnosticReportToDetail(report: DiagnosticReportResource): PatientDiagnosticReportDetail {
  const summary = diagnosticReportToSummary(report);

  return {
    ...summary,
    subjectId: report.subject?.reference
      ? normalizeResourceId('Patient', report.subject.reference)
      : undefined,
    subjectDisplay: report.subject?.display,
    effective: report.effectiveDateTime,
    conclusion: report.conclusion || null,
    // Only include explicit display names in performerDisplay. If a performer
    // has no display, don't fall back to an ID here — that prevents IDs from
    // being shown in PDFs. Keep IDs separately in performerIds if needed.
    performerDisplay: report.performer
      ?.map((entry) => entry.display)
      .filter((value): value is string => Boolean(value)),
    // Store normalized practitioner ids (without resource type) so callers can
    // optionally resolve names when needed.
    performerIds: report.performer
      ?.map((entry) =>
        entry.reference ? normalizeResourceId('Practitioner', entry.reference) : undefined
      )
      .filter((value): value is string => Boolean(value)),
  };
}
/**
 * Build conclusion text from diagnostic report data, for updateDiagnosticReport.
 */
function buildDiagnosticReportConclusion(
  reportData: Partial<CreateDiagnosticReportRequest>
): string {
  const conclusionParts: string[] = [];

  if (reportData.chiefComplaint) {
    conclusionParts.push(`Chief Complaint: ${reportData.chiefComplaint}`);
  }

  if (reportData.historyOfPresentIllness) {
    conclusionParts.push(`\nHistory of Present Illness: ${reportData.historyOfPresentIllness}`);
  }

  if (reportData.diagnoses && reportData.diagnoses.length > 0) {
    conclusionParts.push(`\nDiagnoses:\n${reportData.diagnoses.map((d) => `- ${d}`).join('\n')}`);
  }

  if (reportData.medications && reportData.medications.length > 0) {
    conclusionParts.push(
      `\nMedications:\n${reportData.medications.map((m) => `- ${m}`).join('\n')}`
    );
  }

  if (reportData.labTests && reportData.labTests.length > 0) {
    conclusionParts.push(`\nLab Tests:\n${reportData.labTests.map((t) => `- ${t}`).join('\n')}`);
  }

  if (reportData.plan) {
    conclusionParts.push(`\nSummary: ${reportData.plan}`);
  }

  return conclusionParts.join('\n');
}

/**
 * FHIR Marital Status codes
 * @see http://hl7.org/fhir/R4/valueset-marital-status.html
 */
export type FhirMaritalStatus = 'S' | 'M' | 'D' | 'W' | 'UNK';

/**
 * Marital Status CodeableConcept for FHIR
 */
export const FHIR_MARITAL_STATUS_CODES: Record<string, { code: string; display: string }> = {
  S: { code: 'S', display: 'Never Married' },
  M: { code: 'M', display: 'Married' },
  D: { code: 'D', display: 'Divorced' },
  W: { code: 'W', display: 'Widowed' },
  U: { code: 'UNK', display: 'Unknown' },
};

/**
 * FHIR Patient resource creation request
 */
export interface CreatePatientRequest {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | 'unknown';
  maritalStatus?: 'S' | 'M' | 'D' | 'W' | 'U';
  email?: string;
  phone?: string;
  identifier?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  /** Organization reference (id) */
  organizationId?: string;
  /** Organization display name */
  organizationName?: string;
  /** The patients already registered with this phone were reviewed; register another person anyway. */
  confirmNewPatient?: boolean;
  /** Front desk already decided — from a previous call's `candidates` — this IS one of them. */
  linkPersonId?: string;
  /** Front desk already decided — from a previous call's `candidates` — this is NOT any of them. */
  confirmed?: boolean;
}

/**
 * FHIR Patient resource response
 */
export interface PatientResource {
  resourceType: 'Patient';
  id: string;
  identifier?: Array<{
    system?: string;
    value?: string;
  }>;
  name?: Array<{
    given?: string[];
    family?: string;
    text?: string;
  }>;
  birthDate?: string;
  gender?: string;
  maritalStatus?: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  };
  telecom?: Array<{
    system: string;
    value: string;
  }>;
  address?: Array<{
    line?: string[];
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>;
  active?: boolean;
  deceasedBoolean?: boolean;
  deceasedDateTime?: string;
  managingOrganization?: {
    reference?: string;
    display?: string;
  };
  meta?: {
    lastUpdated?: string;
    account?: {
      reference?: string;
    };
  };
}

/**
 * Observation/Consultation resource for storing consultation notes
 */
export interface CreateConsultationRequest {
  patientId: string;
  practitionerId: string;
  title: string;
  notes: string;
  findings?: string;
  date?: string;
}

export interface ListPatientsParams {
  count?: number;
  offset?: number;
  query?: string;
  gender?: Gender;
  status?: PatientStatus;
  /** Sort field */
  sortBy?: 'name' | 'age' | 'createdAt' | 'lastVisitDate';
  /** Sort order */
  sortOrder?: 'asc' | 'desc';
  /** Filter by specific patient IDs (for practitioner-scoped views) */
  patientIds?: string[];
}

export interface ListPatientsResult {
  patients: PatientResource[];
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface UpdatePatientRequest {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: Gender;
  maritalStatus?: 'S' | 'M' | 'D' | 'W' | 'U';
  email?: string | null;
  phone?: string | null;
  identifier?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  status?: PatientStatus;
  /** Organization reference (id) */
  organizationId?: string;
  /** Organization display name */
  organizationName?: string;
}

/**
 * FHIR Observation resource response
 */
export interface ObservationResource {
  resourceType: 'Observation';
  id: string;
  subject: {
    reference: string;
  };
  performer?: Array<{
    reference: string;
  }>;
  status: string;
  code: {
    coding: Array<{
      system: string;
      code: string;
      display: string;
    }>;
  };
  valueString?: string;
  effectiveDateTime?: string;
}

export interface PatientDiagnosticReportSummary {
  id: string;
  status: DiagnosticReportStatus;
  codeText: string;
  issued?: string;
  encounterId?: string;
  encounterDisplay?: string;
  practitionerName?: string;
  practitionerId?: string;
}

export interface PatientDiagnosticReportDetail extends PatientDiagnosticReportSummary {
  subjectId?: string;
  subjectDisplay?: string;
  effective?: string;
  conclusion?: string | null;
  performerDisplay?: string[];
  /** Normalized practitioner ids (without resource type) when available */
  performerIds?: string[];
}

export interface SoapSection {
  title: string;
  content: string;
}

export interface SoapNoteData {
  /** Flat sections from the Composition (Chief Complaint, HPI, etc.) */
  sections?: SoapSection[];
  [key: string]: string | null | SoapSection[] | undefined;
}

/** DocumentSource field structure */
export interface DocumentSourceField<T = string | unknown[]> {
  value: T;
  timestamp: string;
  source_phrase: string;
  explanation: string;
}

/** DocumentSource data structure for clinical documents */
export interface DocumentSourceData {
  schema_version?: string;
  patient_summary?: DocumentSourceField<string>;
  chief_complaint?: DocumentSourceField<string>;
  history_of_present_illness?: DocumentSourceField<string>;
  past_medical_surgical_history?: DocumentSourceField<string>;
  medication_history?: DocumentSourceField<string>;
  allergies?: DocumentSourceField<string[]>;
  family_history?: DocumentSourceField<
    Array<{ relation: string; condition: string; notes?: string }>
  >;
  social_history?: DocumentSourceField<string>;
  review_of_systems?: DocumentSourceField<string>;
  physical_examination?: DocumentSourceField<
    Array<{ system: string; type: string; value: string; notes?: string }>
  >;
  assessment_and_diagnosis?: DocumentSourceField<Array<{ diagnosis: string; notes?: string }>>;
  investigations_orders?: DocumentSourceField<string[]>;
  treatment_plan_management?: DocumentSourceField<string>;
  medications?: DocumentSourceField<
    Array<{
      medication: string;
      dosage?: { value: string; unit: string };
      frequency?: string;
      timing?: string[];
      duration?: string;
      medicine_type?: string;
      intake_type?: string;
      relation_with_meal?: string;
      composition?: string;
      additional_info?: string;
    }>
  >;
  follow_up_plan?: DocumentSourceField<string>;
  meal_plan?: DocumentSourceField<string>;
  pediatric_milestone?: DocumentSourceField<string>;
  adt_order?: DocumentSourceField<string>;
  cdss_disclaimer?: string;
  pranik_cdss?: {
    betterMedicationSuggestions?: DocumentSourceField<unknown[]>;
    drugInteractionAlerts?: DocumentSourceField<unknown[]>;
    missedSymptomAlerts?: DocumentSourceField<unknown[]>;
    allergyConflictAlerts?: DocumentSourceField<unknown[]>;
  };
  savedAt?: string;
}

/** DocumentReference resource shape */
interface DocumentReferenceResource {
  id: string;
  status: string;
  type?: {
    coding?: Array<{ system?: string; code?: string; display?: string }>;
    text?: string;
  };
  date?: string;
  subject?: { reference?: string; display?: string };
  context?: {
    encounter?: Array<{ reference?: string; display?: string }>;
  };
  content?: Array<{
    attachment?: {
      contentType?: string;
      url?: string;
      data?: string;
      title?: string;
    };
  }>;
  meta?: {
    lastUpdated?: string;
  };
}

/** Minimal FHIR Composition resource shape used internally */
interface CompositionResource {
  id: string;
  status: string;
  title?: string;
  type?: { text?: string };
  date?: string;
  subject?: { reference?: string };
  encounter?: { reference?: string };
  author?: Array<{ reference?: string; display?: string }>;
  section?: Array<{
    title?: string;
    code?: { text?: string; coding?: Array<{ code?: string; display?: string }> };
    text?: { status?: string; div?: string };
    section?: Array<{
      title?: string;
      text?: { status?: string; div?: string };
    }>;
  }>;
}

/**
 * FHIR Resources API Service
 *
 * Provides methods for creating and managing FHIR resources in Medplum:
 * - Create Patient resources
 * - Create Observation resources for consultations
 * - Create DocumentReference for clinical notes
 */

export interface CreatePatientResponse {
  /** Absent when `needsReview` is true — nothing was created yet. */
  patient?: PatientResource;
  personId?: string;
  /** True iff linked to an existing Person rather than a fresh one. */
  linked?: boolean;
  /**
   * True when nothing was created because a decision is needed. Call
   * `createPatient` again with the same data plus the decision:
   *  - `existingPatients` present: patients of this organization already have
   *    this phone → `confirmNewPatient: true` to register another person.
   *  - `candidates` present: people at other organizations have this phone →
   *    `linkPersonId` (same person) or `confirmed: true` (new person).
   */
  needsReview?: boolean;
  existingPatients?: PatientResource[];
  candidates?: PersonCandidate[];
}

/**
 * Patients already registered in the caller's organization, by phone or
 * identifier, through the same BFF lookup the registration endpoint uses for
 * its duplicate check — so a patient listed here is exactly one registration
 * would have matched. Throws on failure: an empty list only ever means "nobody
 * matched".
 */
async function lookupOrgPatients(query: {
  phone?: string;
  identifier?: string;
}): Promise<PatientResource[]> {
  const PRANIK_HMIS_V2 = `${import.meta.env.VITE_AUTH_API_URL}/v2`;
  const response = await apiClient.post<{
    success: boolean;
    patients?: PatientResource[];
    error?: string;
  }>('/patients/lookup', { body: query, baseUrl: PRANIK_HMIS_V2 });

  if (response.data.success === false) {
    throw new Error(response.data.error || 'Patient search failed');
  }
  return response.data.patients ?? [];
}

export const medplumResourcesApi = {
  /**
   * Registers a patient through the pranik-hmis BFF (`POST /v2/patients`)
   * rather than writing `Patient` directly through the FHIR proxy. The BFF
   * resolves the caller's own org server-side, runs same-org duplicate
   * detection, and links the chart to a cross-org Person — none of which a
   * direct FHIR write can do. `organizationId`/`organizationName` are no
   * longer sent: the BFF always registers into the caller's own org.
   *
   * The one registration endpoint (shared with the kiosk). It never returns
   * an existing patient as if it were new:
   *   1. Call with the patient's details. If patients of this organization
   *      already have the phone, or people at other organizations do, the
   *      response is `needsReview: true` with `existingPatients` or
   *      `candidates` — nothing was created.
   *   2. Call again with the SAME data plus the decision (`confirmNewPatient`,
   *      `linkPersonId` or `confirmed`) — this time the patient is created.
   *
   * @example
   * ```tsx
   * const { patient, needsReview, candidates } = await medplumResourcesApi.createPatient({
   *   firstName: 'John',
   *   lastName: 'Doe',
   *   dateOfBirth: '1990-01-01',
   *   gender: 'male'
   * });
   * ```
   */
  createPatient: async (patientData: CreatePatientRequest): Promise<CreatePatientResponse> => {
    const PRANIK_HMIS_V2 = `${import.meta.env.VITE_AUTH_API_URL}/v2`;

    try {
      const body = {
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        dateOfBirth: patientData.dateOfBirth,
        gender: patientData.gender || 'unknown',
        maritalStatus: patientData.maritalStatus,
        email: patientData.email,
        phone: patientData.phone,
        identifier: patientData.identifier,
        address: patientData.address,
        city: patientData.city,
        state: patientData.state,
        postalCode: patientData.postalCode,
        country: patientData.country,
        confirmNewPatient: patientData.confirmNewPatient,
        linkPersonId: patientData.linkPersonId,
        confirmed: patientData.confirmed,
      };

      const response = await apiClient.post<{
        success: boolean;
        patient?: PatientResource;
        personId?: string;
        linked?: boolean;
        needsReview?: boolean;
        existingPatients?: PatientResource[];
        candidates?: PersonCandidate[];
        error?: string;
      }>('/patients', { body, baseUrl: PRANIK_HMIS_V2 });

      if (!response.data.success || (!response.data.patient && !response.data.needsReview)) {
        throw new Error(response.data.error || 'Failed to create patient');
      }

      return {
        patient: response.data.patient,
        personId: response.data.personId,
        linked: response.data.linked,
        needsReview: response.data.needsReview,
        existingPatients: response.data.existingPatients,
        candidates: response.data.candidates,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create patient';
      logger.error('Error creating patient:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Create a consultation note as an Observation resource
   *
   * @param consultationData - Consultation information
   * @returns Promise with created Observation resource
   * @throws {Error} - On creation failure
   *
   * @example
   * ```tsx
   * const observation = await medplumResourcesApi.createConsultation({
   *   patientId: 'Patient/123',
   *   practitionerId: 'Practitioner/456',
   *   title: 'Follow-up consultation',
   *   notes: 'Patient is improving...'
   * });
   * ```
   */
  createConsultation: async (
    consultationData: CreateConsultationRequest
  ): Promise<ObservationResource> => {
    try {
      const patientReference = toResourceReference('Patient', consultationData.patientId);
      const practitionerReference = consultationData.practitionerId
        ? toResourceReference('Practitioner', consultationData.practitionerId)
        : undefined;

      const observationResource = {
        resourceType: 'Observation' as const,
        status: 'final' as const,
        subject: {
          reference: patientReference,
        },
        ...(practitionerReference && {
          performer: [
            {
              reference: practitionerReference,
            },
          ],
        }),
        code: {
          coding: [
            {
              system: 'http://loinc.org',
              code: '11488-4',
              display: 'Consultation note',
            },
          ],
        },
        valueString: `${consultationData.title}\n\n${consultationData.notes}${
          consultationData.findings ? `\n\nFindings:\n${consultationData.findings}` : ''
        }`,
        effectiveDateTime: consultationData.date || new Date().toISOString(),
      };

      const response = await apiClient.post<ObservationResource>('/Observation', {
        body: observationResource,
      });

      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create consultation';
      logger.error('Error creating consultation:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * List patients with optional filters and pagination
   */
  listPatients: async (params: ListPatientsParams = {}): Promise<ListPatientsResult> => {
    try {
      const {
        count = 20,
        offset = 0,
        query,
        gender,
        status,
        sortBy,
        sortOrder = 'desc',
        patientIds,
      } = params;

      let patients: PatientResource[] = [];
      let total = 0;
      let hasNext = false;
      let hasPrev = false;

      // Map sort field to FHIR sort parameter
      const getSortParam = () => {
        const direction = sortOrder === 'asc' ? '' : '-';
        switch (sortBy) {
          case 'name':
            return `${direction}name`;
          case 'age':
            // Sort by birthDate (ascending birthDate = oldest first, so reverse for age)
            return sortOrder === 'asc' ? '-birthdate' : 'birthdate';
          case 'createdAt':
            return `${direction}_lastUpdated`;
          case 'lastVisitDate':
            return `${direction}_lastUpdated`;
          default:
            return '-_lastUpdated';
        }
      };

      // Build base search params (without query)
      const buildBaseParams = (doubleCount: boolean = false, idChunk?: string[]) => {
        const searchParams = new URLSearchParams();
        // Only double count when combining multiple searches (query path)
        // For non-query path, use the original count for correct pagination
        searchParams.set('_count', String(doubleCount ? count * 2 : count));
        searchParams.set('_sort', getSortParam());
        searchParams.set('_total', 'accurate');
        if (offset > 0) {
          searchParams.set('_offset', String(offset));
        }
        if (gender) {
          searchParams.set('gender', mapAppGenderToMedplum(gender));
        }
        if (status === 'active') {
          searchParams.set('active', 'true');
        } else if (status === 'inactive') {
          searchParams.set('active', 'false');
        } else if (status === 'deceased') {
          searchParams.set('deceased', 'true');
        }
        // Filter by specific patient IDs (for practitioner-scoped views)
        // Use provided chunk or all IDs if small enough
        if (idChunk) {
          searchParams.set('_id', idChunk.join(','));
        } else if (patientIds && patientIds.length > 0 && patientIds.length <= 20) {
          searchParams.set('_id', patientIds.join(','));
        }
        return searchParams;
      };

      // Chunk patient IDs to avoid URL length limits (max ~20 IDs per request)
      const CHUNK_SIZE = 20;
      const shouldChunkPatientIds = patientIds && patientIds.length > CHUNK_SIZE;
      const patientIdChunks = shouldChunkPatientIds
        ? Array.from({ length: Math.ceil(patientIds.length / CHUNK_SIZE) }, (_, i) =>
            patientIds.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
          )
        : [];

      if (query && query.trim().length > 0) {
        const trimmedQuery = query.trim().toLowerCase();

        // If we have a large patient ID list with a query, we need to fetch all patients first
        // then filter by query client-side to avoid URL length limits
        if (shouldChunkPatientIds && patientIds) {
          logger.log(
            'Query with large patient ID list - fetching all patients first, then filtering'
          );
          const chunkResults = await Promise.all(
            patientIdChunks.map(async (chunk) => {
              const searchParams = new URLSearchParams();
              searchParams.set('_count', String(chunk.length));
              searchParams.set('_id', chunk.join(','));
              const response = await apiClient.get<any>(`/Patient?${searchParams.toString()}`);
              return response.data;
            })
          );
          // Combine and deduplicate
          const seenIds = new Set<string>();
          const allPatients: PatientResource[] = [];
          for (const bundle of chunkResults) {
            const entries = (bundle.entry || []) as Array<{ resource?: PatientResource }>;
            for (const entry of entries) {
              if (entry.resource?.id && !seenIds.has(entry.resource.id)) {
                seenIds.add(entry.resource.id);
                allPatients.push(entry.resource);
              }
            }
          }
          // Filter by query client-side
          const filteredPatients = allPatients.filter((patient) => {
            const nameMatch = patient.name?.some((name) =>
              `${name.given?.join(' ') || ''} ${name.family || ''}`
                .toLowerCase()
                .includes(trimmedQuery)
            );
            const phoneMatch = patient.telecom?.some((contact) =>
              contact.value?.toLowerCase().includes(trimmedQuery)
            );
            const identifierMatch = patient.identifier?.some((id) =>
              id.value?.toLowerCase().includes(trimmedQuery)
            );
            return nameMatch || phoneMatch || identifierMatch;
          });
          // Sort
          if (sortBy === 'name') {
            filteredPatients.sort((a, b) => {
              const nameA =
                `${a.name?.[0]?.family || ''} ${a.name?.[0]?.given?.join(' ') || ''}`.toLowerCase();
              const nameB =
                `${b.name?.[0]?.family || ''} ${b.name?.[0]?.given?.join(' ') || ''}`.toLowerCase();
              return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });
          } else if (sortBy === 'age') {
            filteredPatients.sort((a, b) => {
              const dateA = a.birthDate || '';
              const dateB = b.birthDate || '';
              return sortOrder === 'asc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
            });
          } else {
            filteredPatients.sort((a, b) => {
              const dateA = a.meta?.lastUpdated || '';
              const dateB = b.meta?.lastUpdated || '';
              return sortOrder === 'asc' ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
            });
          }
          total = filteredPatients.length;
          patients = filteredPatients.slice(offset, offset + count);
          hasNext = offset + count < total;
          hasPrev = offset > 0;
        } else {
          // Search by name
          const nameParams = buildBaseParams(true); // Double count to account for combining searches
          nameParams.set('name:contains', trimmedQuery);
          const nameResponse = await apiClient.get<any>(`/Patient?${nameParams.toString()}`);
          const nameBundle = nameResponse.data;
          const nameEntries = (nameBundle.entry || []) as Array<{ resource?: PatientResource }>;
          const namePatients = nameEntries
            .map((entry) => entry.resource)
            .filter((resource): resource is PatientResource => !!resource);

          // Search by phone/telecom - try different formats
          let phonePatients: PatientResource[] = [];
          try {
            // Try format: telecom=phone|value
            const phoneParams1 = buildBaseParams(true); // Double count to account for combining searches
            const phoneValues = phoneSearchValues(trimmedQuery);
            phoneParams1.set(
              'telecom',
              (phoneValues.length ? phoneValues : [trimmedQuery]).map((v) => `phone|${v}`).join(',')
            );
            const phoneResponse1 = await apiClient.get<any>(`/Patient?${phoneParams1.toString()}`);
            const phoneBundle1 = phoneResponse1.data;
            const phoneEntries1 = (phoneBundle1.entry || []) as Array<{
              resource?: PatientResource;
            }>;
            phonePatients = phoneEntries1
              .map((entry) => entry.resource)
              .filter((resource): resource is PatientResource => !!resource);
          } catch (error) {
            logger.warn('Phone search format 1 failed, trying format 2:', error);
            try {
              // Try format: telecom=value (without system)
              const phoneParams2 = buildBaseParams(true); // Double count to account for combining searches
              phoneParams2.set('telecom', trimmedQuery);
              logger.log(
                'Searching patients by phone (format 2) with params:',
                phoneParams2.toString()
              );
              const phoneResponse2 = await apiClient.get<any>(
                `/Patient?${phoneParams2.toString()}`
              );
              const phoneBundle2 = phoneResponse2.data;
              const phoneEntries2 = (phoneBundle2.entry || []) as Array<{
                resource?: PatientResource;
              }>;
              phonePatients = phoneEntries2
                .map((entry) => entry.resource)
                .filter((resource): resource is PatientResource => !!resource);
            } catch (error2) {
              logger.warn('Phone search format 2 also failed:', error2);
              // If both fail, we'll rely on client-side filtering
            }
          }

          // Search by identifier
          let identifierPatients: PatientResource[] = [];
          try {
            const identifierParams = buildBaseParams(true); // Double count to account for combining searches
            identifierParams.set('identifier', trimmedQuery);
            const identifierResponse = await apiClient.get<any>(
              `/Patient?${identifierParams.toString()}`
            );
            const identifierBundle = identifierResponse.data;
            const identifierEntries = (identifierBundle.entry || []) as Array<{
              resource?: PatientResource;
            }>;
            identifierPatients = identifierEntries
              .map((entry) => entry.resource)
              .filter((resource): resource is PatientResource => !!resource);
          } catch (error) {
            logger.warn('Identifier search failed:', error);
            // If identifier search fails, we'll rely on name and phone results
          }

          // Combine results from all three searches and deduplicate
          const allPatients = [...namePatients, ...phonePatients, ...identifierPatients];
          let combinedPatients = allPatients.filter(
            (p, index, self) => p.id && index === self.findIndex((other) => other.id === p.id)
          );

          // Additional client-side filtering to catch any matches that weren't found by server search
          // This ensures we catch all matches even if the server search didn't work perfectly
          combinedPatients = combinedPatients.filter((patient) => {
            const nameMatch = patient.name?.some((name) =>
              `${name.given?.join(' ') || ''} ${name.family || ''}`
                .toLowerCase()
                .includes(trimmedQuery)
            );
            const phoneMatch = patient.telecom?.some((contact) =>
              contact.value?.toLowerCase().includes(trimmedQuery)
            );
            const identifierMatch = patient.identifier?.some((id) =>
              id.value?.toLowerCase().includes(trimmedQuery)
            );
            return nameMatch || phoneMatch || identifierMatch;
          });

          patients = combinedPatients;

          // Use the larger total from either search, or estimate based on combined results
          const nameTotal =
            typeof nameBundle.total === 'number' ? nameBundle.total : namePatients.length;
          total = nameTotal; // Use name search total as primary

          // Check pagination links from name search (primary search)
          const links = nameBundle.link || [];
          hasNext = links.some((link: { relation: string }) => link.relation === 'next');
          hasPrev =
            links.some(
              (link: { relation: string }) =>
                link.relation === 'previous' || link.relation === 'prev'
            ) || offset > 0;
        }
      } else if (shouldChunkPatientIds && patientIds) {
        // Large patient ID list — fetch in chunks to avoid URL length limits
        logger.log(
          'Chunking patient ID fetch:',
          patientIds.length,
          'IDs in',
          patientIdChunks.length,
          'chunks'
        );
        const chunkResults = await Promise.all(
          patientIdChunks.map(async (chunk) => {
            const searchParams = buildBaseParams(false, chunk);
            // Fetch the full chunk; pagination is applied client-side after aggregation.
            searchParams.set('_count', String(chunk.length));
            searchParams.delete('_offset');
            const response = await apiClient.get<any>(`/Patient?${searchParams.toString()}`);
            return response.data;
          })
        );
        // Combine entries from all chunks and deduplicate by ID
        const seenIds = new Set<string>();
        const allEntries: PatientResource[] = [];
        for (const bundle of chunkResults) {
          const entries = (bundle.entry || []) as Array<{ resource?: PatientResource }>;
          for (const entry of entries) {
            if (entry.resource?.id && !seenIds.has(entry.resource.id)) {
              seenIds.add(entry.resource.id);
              allEntries.push(entry.resource);
            }
          }
        }
        // Apply client-side sort and pagination since server-side sort/pagination
        // doesn't work correctly across chunked results
        if (sortBy === 'name') {
          allEntries.sort((a, b) => {
            const nameA =
              `${a.name?.[0]?.family || ''} ${a.name?.[0]?.given?.join(' ') || ''}`.toLowerCase();
            const nameB =
              `${b.name?.[0]?.family || ''} ${b.name?.[0]?.given?.join(' ') || ''}`.toLowerCase();
            return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
          });
        } else if (sortBy === 'age') {
          allEntries.sort((a, b) => {
            const dateA = a.birthDate || '';
            const dateB = b.birthDate || '';
            return sortOrder === 'asc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
          });
        } else {
          // Default: sort by _lastUpdated
          allEntries.sort((a, b) => {
            const dateA = a.meta?.lastUpdated || '';
            const dateB = b.meta?.lastUpdated || '';
            return sortOrder === 'asc' ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
          });
        }
        total = allEntries.length;
        patients = allEntries.slice(offset, offset + count);
        hasNext = offset + count < total;
        hasPrev = offset > 0;
      } else {
        // No query - just use base params (don't double count for correct pagination)
        const searchParams = buildBaseParams(false);
        const response = await apiClient.get<any>(`/Patient?${searchParams.toString()}`);
        const bundle = response.data;
        const entries = (bundle.entry || []) as Array<{ resource?: PatientResource }>;
        patients = entries
          .map((entry) => entry.resource)
          .filter((resource): resource is PatientResource => !!resource);
        total = typeof bundle.total === 'number' ? bundle.total : patients.length + offset;
        const links = bundle.link || [];
        hasNext = links.some((link: { relation: string }) => link.relation === 'next');
        hasPrev =
          links.some(
            (link: { relation: string }) => link.relation === 'previous' || link.relation === 'prev'
          ) || offset > 0;
      }

      return {
        patients,
        total,
        hasNext,
        hasPrev,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to list patients';
      logger.error('Error listing patients:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get a patient by ID
   *
   * @param patientId - Patient ID
   * @returns Promise with Patient resource
   * @throws {Error} - If patient not found or access denied
   *
   * @example
   * ```tsx
   * const patient = await medplumResourcesApi.getPatient('Patient/123');
   * ```
   */
  getPatient: async (patientId: string): Promise<PatientResource> => {
    try {
      const normalizedId = normalizeResourceId('Patient', patientId);
      logger.log('Fetching patient:', normalizedId);

      const response = await apiClient.get<PatientResource>(`/Patient/${normalizedId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch patient';
      logger.error('Error fetching patient:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get multiple patients by IDs in a single batch request
   * OPTIMIZED: Uses FHIR's _id parameter to fetch patients in batched API calls
   * Handles large numbers of patients by chunking to avoid URL length limits
   *
   * @param patientIds - Array of Patient IDs
   * @returns Promise with Map of patientId -> PatientResource
   */
  getPatientsByIds: async (patientIds: string[]): Promise<Map<string, PatientResource>> => {
    const patientMap = new Map<string, PatientResource>();

    if (patientIds.length === 0) {
      return patientMap;
    }

    try {
      // Normalize all IDs
      const normalizedIds = patientIds.map((id) => normalizeResourceId('Patient', id));

      // Chunk IDs to avoid URL length limits (max ~20 IDs per request to stay under 2000 char URL)
      const CHUNK_SIZE = 20;
      const chunks: string[][] = [];
      for (let i = 0; i < normalizedIds.length; i += CHUNK_SIZE) {
        chunks.push(normalizedIds.slice(i, i + CHUNK_SIZE));
      }

      logger.log('Batch fetching patients:', normalizedIds.length, 'in', chunks.length, 'chunks');

      // Fetch all chunks in parallel
      const chunkResults = await Promise.all(
        chunks.map(async (chunk) => {
          const searchParams = new URLSearchParams();
          searchParams.set('_id', chunk.join(','));
          searchParams.set('_count', String(chunk.length));

          const response = await apiClient.get<any>(`/Patient?${searchParams.toString()}`);
          const bundle = response.data;
          return (bundle.entry || []) as Array<{ resource?: PatientResource }>;
        })
      );

      // Combine results from all chunks
      chunkResults.forEach((entries) => {
        entries.forEach((entry) => {
          if (entry.resource?.id) {
            patientMap.set(entry.resource.id, entry.resource);
          }
        });
      });

      logger.log('Batch fetched patients:', patientMap.size);
      return patientMap;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to batch fetch patients';
      logger.error('Error batch fetching patients:', errorMessage);
      // Return empty map rather than throwing - graceful degradation
      return patientMap;
    }
  },

  /**
   * List diagnostic reports for a patient
   */
  listDiagnosticReportsByPatient: async (
    patientId: string,
    options: { count?: number; resolveNames?: boolean } = {}
  ): Promise<PatientDiagnosticReportSummary[]> => {
    try {
      const reference = toResourceReference('Patient', patientId);
      const { count = 1000, resolveNames = false } = options;

      const searchParams = new URLSearchParams();
      searchParams.set('subject', reference);
      searchParams.set('_count', String(count));
      searchParams.set('_sort', '-date');
      searchParams.set('_total', 'none');

      const response = await apiClient.get<any>(`/DiagnosticReport?${searchParams.toString()}`);
      const bundle = response.data;
      const reports = (bundle.entry || [])
        .map(
          (entry: { resource: DiagnosticReportResource | undefined }) =>
            entry.resource as DiagnosticReportResource | undefined
        )
        .filter(
          (resource: DiagnosticReportResource | undefined): resource is DiagnosticReportResource =>
            !!resource
        );

      const summaries = reports.map(diagnosticReportToSummary);

      if (resolveNames) {
        await Promise.all(
          summaries.map(async (summary: PatientDiagnosticReportSummary) => {
            if (!summary.practitionerName && summary.practitionerId) {
              summary.practitionerName = await resolvePractitionerName(summary.practitionerId);
            }
          })
        );
      }

      return summaries;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to list diagnostic reports for patient';
      logger.error('Error listing diagnostic reports:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  getDiagnosticReport: async (reportId: string): Promise<PatientDiagnosticReportDetail> => {
    try {
      const normalizedId = normalizeResourceId('DiagnosticReport', reportId);
      logger.log('Fetching diagnostic report:', normalizedId);

      const response = await apiClient.get<DiagnosticReportResource>(
        `/DiagnosticReport/${normalizedId}`
      );
      return diagnosticReportToDetail(response.data);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch diagnostic report';
      logger.error('Error fetching diagnostic report:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  getLatestDiagnosticReportForEncounter: async (
    encounterId: string
  ): Promise<PatientDiagnosticReportDetail | null> => {
    try {
      const reference = toResourceReference('Encounter', encounterId);
      const searchParams = new URLSearchParams();
      searchParams.set('encounter', reference);
      searchParams.set('_count', '1');
      searchParams.set('_sort', '-date');
      searchParams.set('_total', 'none');

      logger.log('Fetching latest diagnostic report for encounter:', reference);

      const response = await apiClient.get<any>(`/DiagnosticReport?${searchParams.toString()}`);
      const bundle = response.data;
      const reportResource = (bundle.entry?.[0]?.resource ||
        null) as DiagnosticReportResource | null;

      if (!reportResource) {
        return null;
      }

      return diagnosticReportToDetail(reportResource);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch diagnostic report for encounter';
      logger.error('Error fetching diagnostic report for encounter:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Fetch the latest FHIR Composition (AI SOAP note) for a given encounter.
   * Returns extracted SOAP data or null if none found.
   */
  getLatestCompositionForEncounter: async (encounterId: string): Promise<SoapNoteData | null> => {
    try {
      const reference = toResourceReference('Encounter', encounterId);
      const searchParams = new URLSearchParams();
      searchParams.set('encounter', reference);
      searchParams.set('_count', '1');
      searchParams.set('_sort', '-date');
      searchParams.set('_total', 'none');

      logger.log('Fetching latest Composition for encounter:', reference);

      const response = await apiClient.get<{ entry?: Array<{ resource?: CompositionResource }> }>(
        `/Composition?${searchParams.toString()}`
      );
      const bundle = response.data;
      const entries = bundle.entry || [];
      logger.log(
        `Composition search returned ${entries.length} result(s) for encounter:`,
        reference
      );

      const compositionResource = (entries[0]?.resource || null) as CompositionResource | null;

      if (!compositionResource) {
        logger.log('No Composition found for encounter:', encounterId);
        return null;
      }

      logger.log(
        'Composition found:',
        compositionResource.id,
        'sections:',
        compositionResource.section?.length
      );
      const soapData = compositionToSoapData(compositionResource);
      logger.log('Parsed SOAP data:', {
        sectionCount: soapData.sections?.length ?? 0,
      });
      return soapData;
    } catch (error) {
      logger.error('Error fetching Composition for encounter:', error);
      return null;
    }
  },

  /**
   * Whether an encounter's consultation summary has been written to Medplum — used to
   * tell when a Quick Submit is ready to verify. The agent writes the summary as a
   * "Consultation" Composition (saveSummaryToFhir shape: title "Outpatient Consultation").
   * The kiosk's pre-consult SOAP Composition for the same encounter is deliberately not counted.
   */
  hasConsultationSummaryForEncounter: async (encounterId: string): Promise<boolean> => {
    const reference = toResourceReference('Encounter', encounterId);
    const searchParams = new URLSearchParams();
    searchParams.set('encounter', reference);
    searchParams.set('_count', '20');
    searchParams.set('_total', 'none');

    const compositionResponse = await apiClient.get<{
      entry?: Array<{ resource?: CompositionResource }>;
    }>(`/Composition?${searchParams.toString()}`);

    return (compositionResponse.data.entry || []).some(
      (entry) =>
        entry.resource?.title === 'Outpatient Consultation' ||
        entry.resource?.type?.text === 'Outpatient consultation note'
    );
  },

  /**
   * List FHIR Composition resources (AI SOAP notes) for a patient.
   * Returns summaries in the same shape as listDiagnosticReportsByPatient for merging.
   */
  listCompositionsByPatient: async (
    patientId: string,
    options: { count?: number; resolveNames?: boolean } = {}
  ): Promise<PatientDiagnosticReportSummary[]> => {
    try {
      const reference = toResourceReference('Patient', patientId);
      const { count = 1000, resolveNames = false } = options;

      const searchParams = new URLSearchParams();
      searchParams.set('patient', reference);
      searchParams.set('_count', String(count));
      searchParams.set('_sort', '-date');
      searchParams.set('_total', 'none');

      const response = await apiClient.get<{ entry?: Array<{ resource?: CompositionResource }> }>(
        `/Composition?${searchParams.toString()}`
      );
      const bundle = response.data;
      const compositions = (bundle.entry || [])
        .map((entry) => entry.resource)
        .filter((r): r is CompositionResource => !!r);

      const summaries = compositions.map(compositionToSummary);

      if (resolveNames) {
        await Promise.all(
          summaries.map(async (summary: PatientDiagnosticReportSummary) => {
            if (!summary.practitionerName && summary.practitionerId) {
              summary.practitionerName = await resolvePractitionerName(summary.practitionerId);
            }
          })
        );
      }

      return summaries;
    } catch (error) {
      logger.error('Error listing Compositions for patient:', error);
      return [];
    }
  },

  /**
   * List FHIR DocumentReference resources (DocumentSource clinical documents) for a patient.
   * Returns summaries in the same shape as listDiagnosticReportsByPatient for merging.
   */
  listDocumentSourcesByPatient: async (
    patientId: string,
    options: { count?: number } = {}
  ): Promise<PatientDiagnosticReportSummary[]> => {
    try {
      const reference = toResourceReference('Patient', patientId);
      const { count = 1000 } = options;

      const searchParams = new URLSearchParams();
      searchParams.set('patient', reference);
      searchParams.set('_count', String(count));
      searchParams.set('_sort', '-date');
      searchParams.set('_total', 'none');

      const response = await apiClient.get<{
        entry?: Array<{ resource?: DocumentReferenceResource }>;
      }>(`/DocumentReference?${searchParams.toString()}`);
      const bundle = response.data;
      const docRefs = (bundle.entry || [])
        .map((entry) => entry.resource)
        .filter((r): r is DocumentReferenceResource => !!r);

      return docRefs.map(documentReferenceToSummary);
    } catch (error) {
      logger.error('Error listing DocumentReferences for patient:', error);
      return [];
    }
  },

  /**
   * Fetch a single FHIR Composition resource and return its SOAP data.
   */
  getCompositionDetail: async (compositionId: string): Promise<SoapNoteData | null> => {
    try {
      const normalizedId = normalizeResourceId('Composition', compositionId);
      logger.log('Fetching Composition detail:', normalizedId);

      const response = await apiClient.get<CompositionResource>(`/Composition/${normalizedId}`);
      return compositionToSoapData(response.data);
    } catch (error) {
      logger.error('Error fetching Composition detail:', error);
      return null;
    }
  },

  /**
   * Update the narrative of a Composition's sections, matched by section title.
   * Only `text.div` of the matching sections is replaced — codes, entries, nested
   * sections and every other Composition field are written back unchanged.
   *
   * @param sectionHtmlByTitle - New HTML per section title (as shown in the visit view)
   */
  updateComposition: async (
    compositionId: string,
    sectionHtmlByTitle: Record<string, string>
  ): Promise<void> => {
    try {
      const normalizedId = normalizeResourceId('Composition', compositionId);
      logger.log('Updating Composition:', normalizedId);

      const response = await apiClient.get<CompositionResource>(`/Composition/${normalizedId}`);
      const existing = response.data;

      const updated: CompositionResource = {
        ...existing,
        section: existing.section?.map((s) => {
          const title = s.title || s.code?.text;
          if (!title || !(title in sectionHtmlByTitle)) return s;
          return {
            ...s,
            text: {
              status: s.text?.status || 'additional',
              div: `<div xmlns="http://www.w3.org/1999/xhtml">${sectionHtmlByTitle[title]}</div>`,
            },
          };
        }),
      };

      await apiClient.put<CompositionResource>(`/Composition/${normalizedId}`, {
        body: updated,
      });
    } catch (error) {
      logger.error('Error updating Composition:', error);
      throw error;
    }
  },

  /**
   * Find the most recent encounter for a patient that has a Composition (SOAP note)
   * and is still open (not finished/cancelled).
   * Returns the encounter ID if found, null otherwise.
   */
  getEncounterWithComposition: async (patientId: string): Promise<string | null> => {
    try {
      // Find all Compositions for this patient, sorted by date descending
      const compositions = await medplumResourcesApi.listCompositionsByPatient(patientId, {
        count: 1,
      });

      if (compositions.length === 0) {
        logger.log('No compositions found for patient:', patientId);
        return null;
      }

      // Get the most recent Composition's encounter ID
      const latestComposition = compositions[0];
      const encounterId = latestComposition.encounterId;

      if (!encounterId) {
        logger.log('Latest composition has no encounter reference');
        return null;
      }

      // Fetch the Encounter to check its status — don't reopen finished/cancelled visits
      const encounter = await medplumResourcesApi.getEncounter(encounterId);
      const status = encounter.status;

      if (['finished', 'cancelled', 'entered-in-error'].includes(status)) {
        logger.log(
          'Encounter with SOAP notes is already closed (status:',
          status,
          '), will create a new one'
        );
        return null;
      }

      logger.log('Found open encounter with SOAP notes:', encounterId, 'status:', status);
      return encounterId;
    } catch (error) {
      logger.error('Error finding encounter with composition:', error);
      return null;
    }
  },

  /**
   * Search for patients by name
   *
   * @param name - Patient name to search
   * @returns Promise with array of matching Patient resources
   * @throws {Error} - On search failure
   *
   * @example
   * ```tsx
   * const patients = await medplumResourcesApi.searchPatients('John Doe');
   * ```
   */
  searchPatients: async (name: string): Promise<PatientResource[]> => {
    try {
      const result = await medplumResourcesApi.listPatients({ query: name, count: 20 });
      return result.patients;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search patients';
      logger.error('Error searching patients:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Search for patients by phone number, in any format a person might type
   * (bare 10-digit, +91…, leading 0, spaces, dashes). Matches the number
   * whether it was stored as +91… or as bare ten digits.
   *
   * @param phoneNumber - Phone number in any common format (e.g., '9876543210' or '+919876543210')
   * @returns Promise with array of matching Patient resources
   * @throws {Error} - On search failure
   *
   * @example
   * ```tsx
   * const patients = await medplumResourcesApi.searchPatientByPhone('9876543210');
   * if (patients.length > 0) {
   *   logger.log('Patient found:', patients[0].id);
   * }
   * ```
   */
  searchPatientByPhone: async (phoneNumber: string): Promise<PatientResource[]> => {
    if (!toE164India(phoneNumber)) return [];
    return lookupOrgPatients({ phone: phoneNumber });
  },

  /**
   * Find patients by identifier value
   */
  findPatientsByIdentifier: async (identifier: string): Promise<PatientResource[]> => {
    const trimmed = identifier.trim();
    if (!trimmed) return [];
    return lookupOrgPatients({ identifier: trimmed });
  },

  /**
   * Search for patients by identifier value.
   *
   * Uses plain identifier search: `identifier=<value>`
   *
   * @param value - Identifier value to search for
   * @returns Promise with array of matching Patient resources
   */
  searchPatientByIdentifier: async (value: string): Promise<PatientResource[]> => {
    const trimmed = value.trim();
    if (!trimmed) return [];
    return lookupOrgPatients({ identifier: trimmed });
  },

  /**
   * Update a patient resource
   */
  updatePatient: async (
    patientId: string,
    updateData: UpdatePatientRequest
  ): Promise<PatientResource> => {
    try {
      const normalizedId = normalizeResourceId('Patient', patientId);
      logger.log('Updating patient:', normalizedId);

      const response = await apiClient.get<PatientResource>(`/Patient/${normalizedId}`);
      const existing = response.data;
      const updated: PatientResource = { ...existing };

      if (updateData.firstName || updateData.lastName) {
        const nameEntry = updated.name?.[0] || {};
        const given = updateData.firstName
          ? [updateData.firstName]
          : nameEntry.given && nameEntry.given.length > 0
            ? nameEntry.given
            : undefined;
        updated.name = [
          {
            ...nameEntry,
            ...(given && { given }),
            ...(updateData.lastName !== undefined && { family: updateData.lastName || undefined }),
          },
        ];
      }

      if (updateData.dateOfBirth !== undefined) {
        updated.birthDate = updateData.dateOfBirth || undefined;
      }

      if (updateData.gender) {
        updated.gender = mapAppGenderToMedplum(updateData.gender);
      }

      if (updateData.identifier !== undefined) {
        if (updateData.identifier) {
          updated.identifier = [
            {
              value: updateData.identifier,
            },
          ];
        } else {
          delete updated.identifier;
        }
      }

      if (updateData.status) {
        if (updateData.status === 'deceased') {
          updated.deceasedBoolean = true;
          updated.active = false;
          updated.deceasedDateTime = updated.deceasedDateTime || new Date().toISOString();
        } else {
          updated.deceasedBoolean = false;
          delete updated.deceasedDateTime;
          updated.active = updateData.status === 'active';
          if (updateData.status === 'inactive') {
            updated.active = false;
          }
        }
      }

      // Update marital status if provided
      if (updateData.maritalStatus !== undefined) {
        if (updateData.maritalStatus) {
          updated.maritalStatus = {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',
                code: FHIR_MARITAL_STATUS_CODES[updateData.maritalStatus]?.code || 'UNK',
                display: FHIR_MARITAL_STATUS_CODES[updateData.maritalStatus]?.display || 'Unknown',
              },
            ],
            text: FHIR_MARITAL_STATUS_CODES[updateData.maritalStatus]?.display || 'Unknown',
          };
        } else {
          delete updated.maritalStatus;
        }
      }

      const currentPhone = getTelecomValue(existing, 'phone');
      const currentEmail = getTelecomValue(existing, 'email');

      if (updateData.phone !== undefined || updateData.email !== undefined) {
        const telecom = (updated.telecom || []).filter(
          (entry) => entry.system !== 'phone' && entry.system !== 'email'
        );

        if (updateData.phone !== undefined) {
          if (updateData.phone) {
            // Patients are always saved with the standard +91 form.
            const phone = toE164India(updateData.phone);
            if (!phone) throw new Error('Invalid phone number: expected 10 digits');
            telecom.push({ system: 'phone', value: phone });
          }
        } else if (currentPhone) {
          telecom.push({ system: 'phone', value: currentPhone });
        }

        if (updateData.email !== undefined) {
          if (updateData.email) {
            telecom.push({ system: 'email', value: updateData.email });
          }
        } else if (currentEmail) {
          telecom.push({ system: 'email', value: currentEmail });
        }

        updated.telecom = telecom.length > 0 ? telecom : undefined;
      }

      if (
        updateData.address !== undefined ||
        updateData.city !== undefined ||
        updateData.state !== undefined ||
        updateData.postalCode !== undefined ||
        updateData.country !== undefined
      ) {
        const lines = updateData.address
          ? updateData.address
              .split(/\r?\n/)
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
          : existing.address?.[0]?.line;

        const primaryAddress = {
          ...existing.address?.[0],
          ...(lines && lines.length > 0 ? { line: lines } : {}),
          ...(updateData.city !== undefined && { city: updateData.city || undefined }),
          ...(updateData.state !== undefined && { state: updateData.state || undefined }),
          ...(updateData.postalCode !== undefined && {
            postalCode: updateData.postalCode || undefined,
          }),
          ...(updateData.country !== undefined && { country: updateData.country || undefined }),
        };

        updated.address = [primaryAddress];
      }

      // Update organization if provided. NOTE: the BFF proxy's org-injection
      // middleware always overwrites `meta.account` with the CALLER'S OWN org
      // on every write (see medplum-proxy.ts), so this can only ever set the
      // organization to the value the proxy would set anyway — it cannot be
      // used to transfer a patient to a *different* org from the browser.
      // Kept in sync with the singular field the proxy enforces (`account`,
      // not the deprecated `accounts[]`) so compartment computation stays
      // consistent with everything else written through this proxy.
      if (updateData.organizationId !== undefined) {
        if (updateData.organizationId) {
          updated.meta = {
            ...updated.meta,
            account: {
              reference: `Organization/${updateData.organizationId}`,
            },
          };
          updated.managingOrganization = {
            reference: `Organization/${updateData.organizationId}`,
            display: updateData.organizationName || undefined,
          };
        } else {
          // Remove organization if explicitly set to null/undefined
          if (updated.meta?.account) {
            delete updated.meta.account;
          }
          delete updated.managingOrganization;
        }
      }

      const updateResponse = await apiClient.put<PatientResource>(`/Patient/${normalizedId}`, {
        body: updated,
      });

      logger.log('Patient updated successfully:', updateResponse.data.id);
      return updateResponse.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update patient';
      logger.error('Error updating patient:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a patient resource
   */
  deletePatient: async (patientId: string): Promise<void> => {
    try {
      const normalizedId = normalizeResourceId('Patient', patientId);
      logger.log('Deleting patient:', normalizedId);
      await apiClient.delete(`/Patient/${normalizedId}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete patient';
      logger.error('Error deleting patient:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Helper: Convert Patient resource to list item
   */
  toPatientListItem: (resource: PatientResource): PatientListItem =>
    patientResourceToListItem(resource),

  /**
   * Helper: Convert Patient resource to demographics detail
   */
  toPatientDemographics: (resource: PatientResource): PatientDemographics =>
    patientResourceToDemographics(resource),

  /**
   * List practitioners with optional filters and pagination
   *
   * @param params - Query parameters for listing practitioners
   * @returns Promise with list of practitioners and pagination info
   * @throws {Error} - On query failure
   *
   * @example
   * ```tsx
   * const result = await medplumResourcesApi.listPractitioners({
   *   count: 20,
   *   offset: 0,
   *   query: 'Dr. Smith'
   * });
   * ```
   */
  listPractitioners: async (
    params: {
      count?: number;
      offset?: number;
      query?: string;
    } = {}
  ): Promise<{
    practitioners: PractitionerResource[];
    total: number;
    hasNext: boolean;
    hasPrev: boolean;
  }> => {
    try {
      const { count = 20, offset = 0, query } = params;

      const searchParams = new URLSearchParams();
      searchParams.set('_count', String(count));
      searchParams.set('_sort', '-_lastUpdated');
      searchParams.set('_total', 'accurate');
      if (offset > 0) {
        searchParams.set('_offset', String(offset));
      }
      if (query && query.trim().length > 0) {
        searchParams.set('name:contains', query.trim());
      }

      logger.log('Listing practitioners with params:', searchParams.toString());

      const response = await apiClient.get<any>(`/Practitioner?${searchParams.toString()}`);
      const bundle = response.data;

      const entries = (bundle.entry || []) as Array<{ resource?: PractitionerResource }>;
      const practitioners = entries
        .map((entry) => entry.resource)
        .filter((resource): resource is PractitionerResource => !!resource);

      const total = typeof bundle.total === 'number' ? bundle.total : practitioners.length + offset;
      const links = bundle.link || [];
      const hasNext = links.some((link: { relation: string }) => link.relation === 'next');
      const hasPrev =
        links.some(
          (link: { relation: string }) => link.relation === 'previous' || link.relation === 'prev'
        ) || offset > 0;

      return {
        practitioners,
        total,
        hasNext,
        hasPrev,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to list practitioners';
      logger.error('Error listing practitioners:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get a practitioner by ID
   *
   * @param practitionerId - Practitioner resource ID
   * @returns Promise with Practitioner resource
   * @throws {Error} - If practitioner not found
   *
   * @example
   * ```tsx
   * const practitioner = await medplumResourcesApi.getPractitioner('Practitioner/123');
   * ```
   */
  getPractitioner: async (practitionerId: string): Promise<PractitionerResource> => {
    try {
      const normalizedId = normalizeResourceId('Practitioner', practitionerId);
      logger.log('Fetching practitioner:', normalizedId);

      const response = await apiClient.get<PractitionerResource>(`/Practitioner/${normalizedId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch practitioner';
      logger.error('Error fetching practitioner:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Reset practitioner password (Admin only)
   * Uses Medplum's project admin API to set password for a user by email
   *
   * @param practitionerId - Practitioner resource ID (used to find associated User)
   * @param newPassword - New password to set (min 8 characters)
   * @returns Promise with success status
   * @throws {Error} - If password reset fails
   *
   * @example
   * ```tsx
   * await medplumResourcesApi.resetPractitionerPassword('Practitioner/123', 'NewSecurePass123!');
   * ```
   */
  resetPractitionerPassword: async (
    practitionerId: string,
    newPassword: string
  ): Promise<{ success: boolean; email?: string; projectId?: string | null }> => {
    try {
      const client = getMedplumClient();
      const normalizedId = normalizeResourceId('Practitioner', practitionerId);
      logger.log('Resetting password for practitioner:', normalizedId);

      // Step 1: Find the ProjectMembership for this practitioner
      const memberships = await client.searchResources('ProjectMembership', {
        profile: `Practitioner/${normalizedId}`,
      });

      logger.log('Found memberships:', memberships.length);

      if (memberships.length === 0) {
        throw new Error('No project membership found for this practitioner');
      }

      const membership = memberships[0];
      logger.log('Membership:', JSON.stringify(membership, null, 2));

      // Step 2: Get the User resource from the membership
      if (!membership.user?.reference) {
        throw new Error('No user associated with this practitioner');
      }

      const userId = membership.user.reference.replace('User/', '');
      logger.log('User ID:', userId);

      const user = await client.readResource('User', userId);
      logger.log('User resource:', JSON.stringify(user, null, 2));

      if (!user.email) {
        throw new Error('No email found for this user');
      }

      // Check if user is project-scoped
      const projectRef = user.project?.reference;
      const projectId = projectRef ? projectRef.replace('Project/', '') : null;

      logger.log('Resetting password for email:', user.email);
      logger.log('User project scope:', projectRef || 'SERVER-SCOPED (no project)');
      if (projectId) {
        logger.warn(
          '⚠️ This is a PROJECT-SCOPED user. You MUST include projectId in login request!'
        );
        logger.warn('⚠️ Project ID required for login:', projectId);
      }

      // Step 3: Use Medplum's project admin API to set the password
      // POST /admin/projects/setpassword with email and password
      const result = await client.post('admin/projects/setpassword', {
        email: user.email,
        password: newPassword,
      });

      logger.log('Password reset API response:', result);
      logger.log('✅ Password reset successful for practitioner:', normalizedId);
      logger.log('✅ User email:', user.email);

      // Show login instructions based on user scope (reuse variables from above)
      if (projectId) {
        logger.log('');
        logger.log('📋 LOGIN INSTRUCTIONS (Project-Scoped User):');
        logger.log('   Email:', user.email);
        logger.log('   Password: [the password you just set]');
        logger.log('   Project ID:', projectId);
        logger.log('   ⚠️ IMPORTANT: Include projectId in your login request!');
        logger.log('');
      } else {
        logger.log('');
        logger.log('📋 LOGIN INSTRUCTIONS (Server-Scoped User):');
        logger.log('   Email:', user.email);
        logger.log('   Password: [the password you just set]');
        logger.log('   No projectId required');
        logger.log('');
      }

      return { success: true, email: user.email, projectId };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      logger.error('❌ Error resetting practitioner password:', errorMessage);
      if (error instanceof Error && 'stack' in error) {
        logger.error('Stack trace:', error.stack);
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Create or find Practitioner resource for logged-in user
   *
   * @param practitionerData - User information from auth
   * @returns Promise with Practitioner resource
   * @throws {Error} - On creation/search failure
   *
   * @example
   * ```tsx
   * const practitioner = await medplumResourcesApi.ensurePractitioner({
   *   userId: user.id,
   *   username: user.username,
   *   firstName: user.firstName,
   *   lastName: user.lastName
   * });
   * ```
   */
  ensurePractitioner: async (
    practitionerData: CreatePractitionerRequest
  ): Promise<PractitionerResource> => {
    try {
      // Search for existing practitioner by user identifier
      logger.log('Searching for practitioner with user ID:', practitionerData.userId);

      const searchParams = new URLSearchParams();
      searchParams.set('identifier', practitionerData.userId);

      const response = await apiClient.get<any>(`/Practitioner?${searchParams.toString()}`);
      const bundle = response.data;

      if (bundle.entry && bundle.entry.length > 0) {
        const practitioner = bundle.entry[0].resource as PractitionerResource;
        logger.log('Found existing practitioner:', practitioner.id);
        return practitioner;
      }

      // Create new practitioner if not found
      logger.log('Creating new practitioner for user:', practitionerData.username);

      const practitionerResource = {
        resourceType: 'Practitioner' as const,
        identifier: [
          {
            system: 'user-id',
            value: practitionerData.userId,
          },
        ],
        name: [
          {
            given: practitionerData.firstName ? [practitionerData.firstName] : undefined,
            family: practitionerData.lastName,
            text: `${practitionerData.firstName || ''} ${practitionerData.lastName || practitionerData.username}`.trim(),
          },
        ],
        ...(practitionerData.email && {
          telecom: [
            {
              system: 'email',
              value: practitionerData.email,
            },
          ],
        }),
      };

      const createResponse = await apiClient.post<PractitionerResource>('/Practitioner', {
        body: practitionerResource,
      });

      logger.log('Practitioner created successfully:', createResponse.data.id);
      return createResponse.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to ensure practitioner';
      logger.error('Error ensuring practitioner:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Create a new Encounter
   *
   * @param encounterData - Encounter information
   * @returns Promise with created Encounter resource
   * @throws {Error} - On creation failure
   *
   * @example
   * ```tsx
   * const encounter = await medplumResourcesApi.createEncounter({
   *   patientId: 'Patient/123',
   *   practitionerId: 'Practitioner/456',
   *   status: 'planned',
   *   class: 'outpatient',
   *   type: 'Initial Consultation',
   *   reasonCode: 'Patient presents with chest pain'
   * });
   * ```
   */
  createEncounter: async (encounterData: CreateEncounterRequest): Promise<EncounterResource> => {
    try {
      logger.log('Creating Encounter for patient:', encounterData.patientId);

      const patientReference = toResourceReference('Patient', encounterData.patientId);
      const practitionerReference = toResourceReference(
        'Practitioner',
        encounterData.practitionerId
      );

      const encounterResource = {
        resourceType: 'Encounter' as const,
        status: encounterData.status,
        class: {
          system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          code: encounterData.class,
          display: encounterData.class.charAt(0).toUpperCase() + encounterData.class.slice(1),
        },
        ...(encounterData.type && {
          type: [
            {
              text: encounterData.type,
            },
          ],
        }),
        subject: {
          reference: patientReference,
        },
        participant: [
          {
            individual: {
              reference: practitionerReference,
            },
            type: [
              {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
                    code: 'PPRF',
                    display: 'Primary Performer',
                  },
                ],
              },
            ],
          },
        ],
        ...(encounterData.period && {
          period: encounterData.period,
        }),
        ...(encounterData.reasonCode && {
          reasonCode: [
            {
              text: encounterData.reasonCode,
            },
          ],
        }),
        ...(encounterData.appointmentId && {
          appointment: [
            {
              reference: toResourceReference('Appointment', encounterData.appointmentId),
            },
          ],
        }),
      };

      const response = await apiClient.post<EncounterResource>('/Encounter', {
        body: encounterResource,
      });

      logger.log('Encounter created successfully:', response.data.id);
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create encounter';
      logger.error('Error creating encounter:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Update an existing Encounter
   *
   * @param encounterId - Encounter resource ID
   * @param updateData - Fields to update
   * @returns Promise with updated Encounter resource
   * @throws {Error} - On update failure
   *
   * @example
   * ```tsx
   * const encounter = await medplumResourcesApi.updateEncounter('Encounter/789', {
   *   status: 'finished',
   *   period: { start: '2025-10-28T10:00:00Z', end: '2025-10-28T11:00:00Z' }
   * });
   * ```
   */
  updateEncounter: async (
    encounterId: string,
    updateData: UpdateEncounterRequest
  ): Promise<EncounterResource> => {
    try {
      logger.log('Updating Encounter:', encounterId);

      // Read current encounter
      const normalizedId = normalizeResourceId('Encounter', encounterId);
      const response = await apiClient.get<EncounterResource>(`/Encounter/${normalizedId}`);
      const encounter = response.data;

      // Merge updates
      const updatedEncounter: EncounterResource = {
        ...encounter,
        ...updateData,
      };

      // Update encounter
      const updateResponse = await apiClient.put<EncounterResource>(`/Encounter/${normalizedId}`, {
        body: updatedEncounter,
      });

      logger.log('Encounter updated successfully:', updateResponse.data.id);
      return updateResponse.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update encounter';
      logger.error('Error updating encounter:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get an Encounter by ID
   *
   * @param encounterId - Encounter resource ID
   * @returns Promise with Encounter resource
   * @throws {Error} - If encounter not found
   *
   * @example
   * ```tsx
   * const encounter = await medplumResourcesApi.getEncounter('Encounter/789');
   * ```
   */
  getEncounter: async (encounterId: string): Promise<EncounterResource> => {
    try {
      logger.log('Fetching encounter:', encounterId);

      const normalizedId = normalizeResourceId('Encounter', encounterId);
      const response = await apiClient.get<EncounterResource>(`/Encounter/${normalizedId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch encounter';
      logger.error('Error fetching encounter:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete an Encounter by ID
   * Used for rollback when subsequent operations fail after encounter creation
   *
   * @param encounterId - Encounter resource ID
   * @returns Promise<void>
   * @throws {Error} - If encounter deletion fails
   *
   * @example
   * ```tsx
   * await medplumResourcesApi.deleteEncounter('abc123');
   * ```
   */
  deleteEncounter: async (encounterId: string): Promise<void> => {
    try {
      logger.log('Deleting encounter:', encounterId);

      const normalizedId = normalizeResourceId('Encounter', encounterId);
      await apiClient.delete(`/Encounter/${normalizedId}`);
      logger.log('Encounter deleted successfully:', normalizedId);
    } catch (error) {
      logger.error('Error deleting encounter:', error);
      throw error;
    }
  },

  /**
   * Delete an Observation resource by ID
   *
   * Used for rollback when subsequent operations fail after observation creation
   *
   * @param observationId - Observation resource ID
   * @returns Promise<void>
   * @throws {Error} - If observation deletion fails
   *
   * @example
   * ```tsx
   * await medplumResourcesApi.deleteObservation('abc123');
   * ```
   */
  deleteObservation: async (observationId: string): Promise<void> => {
    try {
      logger.log('Deleting observation:', observationId);

      const normalizedId = normalizeResourceId('Observation', observationId);
      await apiClient.delete(`/Observation/${normalizedId}`);
      logger.log('Observation deleted successfully:', normalizedId);
    } catch (error) {
      logger.error('Error deleting observation:', error);
      throw error;
    }
  },

  /**
   * List encounters linked to a specific appointment
   *
   * @param appointmentId - Appointment resource ID
   * @returns Promise with array of Encounter resources
   * @throws {Error} - On search failure
   *
   * @example
   * ```tsx
   * const encounters = await medplumResourcesApi.listEncountersByAppointment('Appointment/123');
   * ```
   */
  listEncountersByAppointment: async (appointmentId: string): Promise<EncounterResource[]> => {
    try {
      logger.log('Searching encounters for appointment:', appointmentId);

      const normalizedAppointmentId = normalizeResourceId('Appointment', appointmentId);
      const appointmentReference = toResourceReference('Appointment', normalizedAppointmentId);

      // Search encounters where appointment reference matches
      const searchParams = new URLSearchParams();
      searchParams.set('appointment', appointmentReference);

      const response = await apiClient.get<any>(`/Encounter?${searchParams.toString()}`);
      const bundle = response.data;

      const encounters =
        bundle.entry
          ?.map((entry: { resource?: EncounterResource }) => entry.resource as EncounterResource)
          .filter(Boolean) || [];

      logger.log(`Found ${encounters.length} encounter(s) for appointment ${appointmentId}`);
      return encounters;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to search encounters by appointment';
      logger.error('Error searching encounters by appointment:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get encounter statistics for a practitioner within a date range
   *
   * @param practitionerId - Practitioner resource ID
   * @param startDate - Start of date range (ISO string)
   * @param endDate - End of date range (ISO string)
   * @returns Promise with statistics object containing counts and hours
   *
   * @example
   * ```tsx
   * const stats = await medplumResourcesApi.getPractitionerEncounterStats(
   *   'Practitioner/123',
   *   '2025-01-01T00:00:00Z',
   *   '2025-01-31T23:59:59Z'
   * );
   * // Returns: { newConsultations: 45, followUpVisits: 17, hoursConsulted: 32 }
   * ```
   */
  getPractitionerEncounterStats: async (
    practitionerId: string,
    startDate: string,
    endDate: string
  ): Promise<{
    newConsultations: number;
    followUpVisits: number;
    hoursConsulted: number;
  }> => {
    try {
      logger.log('Fetching encounter stats for practitioner:', practitionerId);

      const normalizedId = normalizeResourceId('Practitioner', practitionerId);
      const practitionerReference = toResourceReference('Practitioner', normalizedId);

      // Search for all encounters for this practitioner within the date range
      // Using date filter for period.start and participant for practitioner
      const searchParams = new URLSearchParams();
      searchParams.set('participant', practitionerReference);
      searchParams.set('date', `ge${startDate}`);
      searchParams.append('date', `le${endDate}`);
      searchParams.set('_count', '1000'); // Get all encounters

      const response = await apiClient.get<any>(`/Encounter?${searchParams.toString()}`);
      const bundle = response.data;

      const encounters =
        bundle.entry
          ?.map((entry: { resource?: EncounterResource }) => entry.resource as EncounterResource)
          .filter(Boolean) || [];

      logger.log(`Found ${encounters.length} encounter(s) for practitioner in date range`);

      // Calculate statistics
      let newConsultations = 0;
      let followUpVisits = 0;
      let totalMinutes = 0;

      // Track patients to identify follow-ups
      const patientEncounterCounts = new Map<string, number>();

      // First pass: count encounters per patient
      encounters.forEach((encounter: EncounterResource) => {
        const patientRef = encounter.subject?.reference;
        if (patientRef) {
          patientEncounterCounts.set(patientRef, (patientEncounterCounts.get(patientRef) || 0) + 1);
        }
      });

      // Second pass: categorize and calculate hours
      encounters.forEach((encounter: EncounterResource) => {
        // Only count finished or in-progress encounters
        if (
          encounter.status === 'finished' ||
          encounter.status === 'in-progress' ||
          encounter.status === 'arrived' ||
          encounter.status === 'triaged'
        ) {
          // Check encounter type for follow-up classification
          const typeText = encounter.type?.[0]?.text?.toLowerCase() || '';
          const isFollowUp =
            typeText.includes('follow-up') ||
            typeText.includes('follow up') ||
            typeText.includes('followup');

          if (isFollowUp) {
            followUpVisits++;
          } else {
            newConsultations++;
          }

          // Calculate duration if period exists
          if (encounter.period?.start && encounter.period?.end) {
            const start = new Date(encounter.period.start);
            const end = new Date(encounter.period.end);
            const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
            if (durationMinutes > 0 && durationMinutes < 720) {
              // Cap at 12 hours to avoid data errors
              totalMinutes += durationMinutes;
            }
          }
        }
      });

      const hoursConsulted = Math.round(totalMinutes / 60);

      logger.log(
        `Stats calculated: ${newConsultations} new, ${followUpVisits} follow-ups, ${hoursConsulted} hours`
      );

      return {
        newConsultations,
        followUpVisits,
        hoursConsulted,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch encounter statistics';
      logger.error('Error fetching encounter statistics:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get all patient IDs that a practitioner has consulted with
   * Used for filtering patient list to show only patients seen by this practitioner
   *
   * @param practitionerId - Practitioner resource ID (with or without 'Practitioner/' prefix)
   * @returns Promise with array of patient IDs (without 'Patient/' prefix)
   *
   * @example
   * ```tsx
   * const patientIds = await medplumResourcesApi.getPractitionerPatientIds('Practitioner/123');
   * // Returns: ['456', '789', '101']
   * ```
   */
  getPractitionerPatientIds: async (practitionerId: string): Promise<string[]> => {
    try {
      const normalizedId = normalizeResourceId('Practitioner', practitionerId);
      const practitionerReference = toResourceReference('Practitioner', normalizedId);

      // Fetch all encounters for this practitioner with manual offset-based pagination
      const searchParams = new URLSearchParams();
      searchParams.set('participant', practitionerReference);
      searchParams.set('_count', '1000');

      const patientIdSet = new Set<string>();
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        searchParams.set('_offset', String(offset));
        const response: any = await apiClient.get<any>(`/Encounter?${searchParams.toString()}`);
        const bundle: any = response.data;

        const encounters =
          bundle.entry
            ?.map((entry: { resource?: EncounterResource }) => entry.resource as EncounterResource)
            .filter(Boolean) || [];

        if (encounters.length === 0) {
          hasMore = false;
        } else {
          encounters.forEach((encounter: EncounterResource) => {
            const patientRef = encounter.subject?.reference;
            if (patientRef) {
              const patientId = normalizeResourceId('Patient', patientRef);
              patientIdSet.add(patientId);
            }
          });

          // Check if there are more results
          const nextLink: any = bundle.link?.find(
            (l: { relation: string }) => l.relation === 'next'
          );
          if (nextLink) {
            offset += encounters.length;
          } else {
            hasMore = false;
          }
        }
      }

      const patientIds = Array.from(patientIdSet);

      return patientIds;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch practitioner patient IDs';
      logger.error('Error fetching practitioner patient IDs:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Update an existing DiagnosticReport
   *
   * @param reportId - ID of the DiagnosticReport to update
   * @param reportData - Updated diagnostic report data
   * @returns Promise with updated DiagnosticReport resource
   * @throws {Error} - On update failure
   *
   * @example
   * ```tsx
   * const updatedReport = await medplumResourcesApi.updateDiagnosticReport('report-123', {
   *   status: 'final',
   *   conclusion: 'Updated consultation summary...',
   *   diagnoses: ['Confirmed diagnosis'],
   *   medications: [{name: 'Aspirin', dosage: '100mg'}]
   * });
   * ```
   */
  updateDiagnosticReport: async (
    reportId: string,
    reportData: Partial<CreateDiagnosticReportRequest>
  ): Promise<DiagnosticReportResource> => {
    try {
      const normalizedId = normalizeResourceId('DiagnosticReport', reportId);

      logger.log('Updating DiagnosticReport:', normalizedId);

      // Fetch existing report
      const response = await apiClient.get<DiagnosticReportResource>(
        `/DiagnosticReport/${normalizedId}`
      );
      const existingReport = response.data;

      // Build updated conclusion text if new data provided
      const conclusionText =
        reportData.conclusion ||
        (reportData.chiefComplaint ||
        reportData.historyOfPresentIllness ||
        reportData.diagnoses ||
        reportData.medications ||
        reportData.labTests ||
        reportData.plan
          ? buildDiagnosticReportConclusion(reportData)
          : undefined);

      // Build updated report (merge with existing)
      const updatedReport: DiagnosticReportResource = {
        ...existingReport,
        ...(reportData.status && { status: reportData.status }),
        ...(reportData.code && {
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '11488-4',
                display: 'Consultation note',
              },
            ],
            text: reportData.code.text,
          },
        }),
        ...(conclusionText && { conclusion: conclusionText }),
        ...(reportData.conclusionCode && { conclusionCode: reportData.conclusionCode }),
        // Update issued timestamp
        issued: new Date().toISOString(),
      };

      const updateResponse = await apiClient.put<DiagnosticReportResource>(
        `/DiagnosticReport/${normalizedId}`,
        {
          body: updatedReport,
        }
      );

      logger.log('DiagnosticReport updated successfully:', updateResponse.data.id);
      return updateResponse.data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update diagnostic report';
      logger.error('Error updating diagnostic report:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * List all projects (for login project selection)
   *
   * NOTE: This function is currently deprecated as projects are hardcoded in auth.ts.
   * If you need dynamic project listing, implement a backend endpoint for this.
   *
   * @deprecated Use AVAILABLE_PROJECTS from auth.ts instead
   * @returns Promise with array of projects
   *
   * @example
   * ```tsx
   * const projects = await medplumResourcesApi.listProjects();
   * ```
   *
   * @deprecated Projects are now fetched dynamically via the V2 login API.
   * This method returns an empty array. Use authApi.login() which returns
   * available projects when requiresProjectSelection is true.
   */
  listProjects: async (): Promise<Array<{ id: string; name: string }>> => {
    try {
      logger.log('=== listProjects: Starting ===');
      logger.warn(
        '⚠️ listProjects is deprecated. Projects are now fetched dynamically via V2 login API.'
      );
      logger.warn('Use authApi.login() - it returns available projects when user has multiple.');
      logger.log('=== listProjects: Complete (returning empty array) ===');

      // Projects are no longer hardcoded - they come from the V2 login API response
      return [];
    } catch (error) {
      logger.error('=== listProjects: ERROR ===');
      logger.error('Error fetching projects:', error);
      logger.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
      return [];
    }
  },

  /**
   * Fetch a ClinicalImpression resource by ID.
   * Returns null if not found or on error.
   */
  getClinicalImpression: async (
    clinicalImpressionId: string
  ): Promise<{ date?: string; note?: Array<{ text?: string }>; [key: string]: unknown } | null> => {
    try {
      const id = clinicalImpressionId.includes('/')
        ? clinicalImpressionId.split('/')[1]
        : clinicalImpressionId;
      const response = await apiClient.get<{ date?: string; note?: Array<{ text?: string }> }>(
        `/ClinicalImpression/${id}`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch ClinicalImpression:', clinicalImpressionId, error);
      return null;
    }
  },
};
